import { filterVisualErrors } from '@/utils/OwnedBundleVisualData';
import type { NodeHandle } from '@/models/logic/RuntimeHandles';
import {
  ensureBundleSignalIdentities,
  containsChipMemberIdentity,
} from './BundleSignalIdentity';
import { Signal } from '@/models/data/Signal';
import { toJS } from 'mobx';
import type { IConnectionModel, ITileManagerModel } from '@/interfaces/models';
import { TileType } from '@/models/TileTypes';
import { tileDefinitionRegistry } from '@/models/tiles/TileRegistry';
import {
  Side,
  TileSideIOType,
  BundleConfigurationSchema,
  BundleInterfaceStateSchema,
} from '@/schemas/tiles';
import { Point } from '@/models/Point';
import { getGlobalDirection, getLocalSide, getNeighborPosition } from './utils';
import { opposite } from '@/utils/direction';
import type { LogicGraph } from './LogicGraph';
import type { TileInstance } from '@/models/tiles/TileInstance';

/** Lower a physical compact wire into ordered Boolean nets. There is no word-size packing. */
export function buildBundleLanes(
  graph: LogicGraph,
  manager: ITileManagerModel,
  connections?: IConnectionModel,
) {
  const tiles = manager.tiles;
  const strict = tiles[0]?.context?.userPreferences?.bundleChipIdentityEnabled === true;
  ensureBundleSignalIdentities(tiles);
  const bundles = tiles.filter((tile) => tile.meta.bundle && !tile.meta.cable);
  for (const tile of tiles)
    if (tile.meta.cable && tile.meta.bundle)
      tile.bundleErrors.push(
        'Cable sizing is automatic; a legacy fixed-lane configuration cannot be applied to this cable',
      );
  const enabled = (tile: TileInstance, side: Side) =>
    connections
      ? connections.connectionFlags(tile.id)[side]
      : (tile.meta.connections?.[side] ?? true);
  for (const tile of bundles) {
    const parsed = BundleConfigurationSchema.safeParse(toJS(tile.meta.bundle));
    if (!parsed.success || tile.tileType !== TileType.Wire) {
      tile.bundleErrors.push(
        'Bundle configuration requires a wire and valid unique lane mappings',
      );
      continue;
    }
    const config = parsed.data;
    const lanes = Array.from({ length: config.width }, (_, lane) => {
      const node = graph.addNode('WIRE');
      node.runtimeKey = JSON.stringify(['bundle', tile.id, lane]);
      return node.id;
    });
    tile.laneNodeIds = {};
    for (const side of Object.values(Side)) {
      const mapping = config.sides?.[side] ?? lanes.map((_, lane) => lane);
      tile.laneNodeIds[side] = mapping.map((lane) => lanes[lane]);
    }
  }
  const diagnosed = new Set<string>();
  for (const tile of tiles) {
    if (!tile.laneNodeIds) continue;
    for (const side of Object.values(Side)) {
      if (!enabled(tile, side)) continue;
      const direction = getGlobalDirection(side, tile.meta.rotation)!;
      const neighbor = manager.get(
        getNeighborPosition(Point.fromData(tile.location), direction),
      );
      if (
        !neighbor ||
        tile.adapterPort ||
        neighbor.adapterPort ||
        tile.meta.cable ||
        neighbor.meta.cable ||
        tile.cablePort ||
        neighbor.cablePort
      )
        continue;
      const neighborSide = getLocalSide(opposite(direction), neighbor.meta.rotation)!;
      if (!enabled(neighbor, neighborSide)) continue;
      // Authored chip structure tiles never acquire implicit inter-pin shorts.
      if (tile.meta.partOfChip && tile.meta.partOfChip === neighbor.meta.partOfChip)
        continue;
      const source = tile.laneNodeIds[side] ?? [];
      const target = neighbor.laneNodeIds
        ? (neighbor.laneNodeIds[neighborSide] ?? [])
        : neighbor.logicNodeIds?.[neighborSide]
          ? [neighbor.logicNodeIds[neighborSide]!]
          : [];
      if (!target.length || !source.length) continue;
      if (source.length !== target.length || !neighbor.laneNodeIds) {
        const key = [tile.id, neighbor.id].sort().join(':');
        if (!diagnosed.has(key)) {
          diagnosed.add(key);
          const error = !neighbor.laneNodeIds
            ? 'Place a Bundle interface between the compact bundle and scalar wire or chip pin'
            : `Width mismatch: ${source.length} lanes on ${side}, ${target.length} at adjacent pin. Choose a matching bundle width or selected-bit branch.`;
          tile.bundleErrors.push(error);
          neighbor.bundleErrors.push(error);
        }
        continue;
      }
      const definition = tileDefinitionRegistry.get(neighbor.tileType);
      const io =
        neighbor.effectiveIoSides?.[neighborSide] ?? definition.ioSides[neighborSide];
      if (!neighbor.laneNodeIds && (!io || io.type === TileSideIOType.NONE)) continue;
      for (let lane = 0; lane < source.length; lane++) {
        if (source[lane] === target[lane]) continue;
        const sourceIsActive = graph.nodes.get(source[lane])?.type !== 'WIRE';
        // Active interface sources (lever/button) output into a boundary; they
        // are never driven backwards by the compact wire.
        const activeSource =
          neighbor.laneNodeIds && graph.nodes.get(target[lane])?.type !== 'WIRE';
        if (
          !activeSource &&
          (neighbor.laneNodeIds ||
            io?.type === TileSideIOType.INPUT ||
            io?.type === TileSideIOType.INPUT_OUTPUT)
        )
          graph.addEdge(source[lane], target[lane], {
            sourceTileId: tile.id,
            targetTileId: neighbor.id,
            targetSide: neighborSide,
          });
        if (
          !sourceIsActive &&
          (neighbor.laneNodeIds ||
            io?.type === TileSideIOType.OUTPUT ||
            io?.type === TileSideIOType.INPUT_OUTPUT)
        )
          graph.addEdge(target[lane], source[lane], {
            sourceTileId: neighbor.id,
            targetTileId: tile.id,
            targetSide: side,
          });
      }
    }
  }
  // Named adapter ports bind only at physical neighbors. One first endpoint
  // names a previously anonymous full-width wire; further endpoints match labels.
  const declaredIdentitySlots = (tile: TileInstance) => {
    const direction = getGlobalDirection(Side.LEFT, tile.meta.rotation)!;
    const neighbor = manager.get(
      getNeighborPosition(Point.fromData(tile.location), direction),
    );
    if (!neighbor?.meta.bundle) return [];
    const side = getLocalSide(opposite(direction), neighbor.meta.rotation)!;
    const config = neighbor.meta.bundle;
    return (
      config.sides?.[side] ?? Array.from({ length: config.width }, (_, lane) => lane)
    ).map((lane) => config.signalIds?.[lane]);
  };
  // Bind the authored identity owner before label-matching other endpoints.
  // Otherwise a stale saved display label could attach a renamed signal first.
  const adapterPorts = tiles
    .filter((tile) => tile.adapterPort && !tile.cablePort)
    .sort((a, b) => {
      const score = (tile: TileInstance) =>
        declaredIdentitySlots(tile).filter(
          (id) => id && tile.adapterSignalIds?.includes(id),
        ).length;
      return score(b) - score(a);
    });
  for (const tile of adapterPorts) {
    if (!enabled(tile, Side.LEFT)) continue;
    const direction = getGlobalDirection(Side.LEFT, tile.meta.rotation)!;
    const neighbor = manager.get(
      getNeighborPosition(Point.fromData(tile.location), direction),
    );
    if (
      !neighbor ||
      (tile.meta.partOfChip && tile.meta.partOfChip === neighbor.meta.partOfChip)
    )
      continue;
    const side = getLocalSide(opposite(direction), neighbor.meta.rotation)!;
    if (!enabled(neighbor, side)) continue;
    const source = tile.laneNodeIds?.[Side.LEFT] ?? [];
    const target = neighbor.laneNodeIds?.[side] ?? [];
    if (!target.length) {
      tile.bundleErrors.push(
        'Connect this bundle port to a compact bundle, not a scalar wire',
      );
      continue;
    }
    refreshBundleSignalCatalog(graph, tiles);
    const names = neighbor.laneSignalIds?.[side]?.map(
      (ids) =>
        new Set(
          ids
            .filter((id) => id.startsWith('adapter:'))
            .map((id) => neighbor.bundleSignalLabels?.[id]),
        ),
    );
    const declared = neighbor.meta.bundle;
    const explicitNames =
      declared &&
      target.map((_, slot) => {
        const lane = declared.sides?.[side]?.[slot] ?? slot;
        return declared.signalLabels?.[declared.signalIds?.[lane] ?? ''];
      });
    const hasNames =
      names?.some((set) => set.size) ||
      explicitNames?.some((label) => label && !/^S\d+$/.test(label));
    const portLabels = (tile.adapterSignalIds ?? []).map(
      (id) => tile.adapterSignalLabels?.[id] ?? '?',
    );
    const identitySlots = declaredIdentitySlots(tile);
    const aliases = neighbor.laneSignalIds?.[side];
    for (let index = 0; index < source.length; index++) {
      const label = portLabels[index];
      const ownId = tile.adapterSignalIds?.[index];
      const candidates = target.filter((_, slot) => {
        if (ownId && (identitySlots[slot] === ownId || aliases?.[slot]?.includes(ownId)))
          return true;
        if (strict) {
          // An anonymous fixed-lane path may be bound positionally once. Once a
          // member is carried, only that member identity can bind this slot.
          const typed = aliases?.[slot]?.some((id) => id.startsWith('adapter:'));
          return !typed && source.length === target.length && slot === index;
        }
        // A connected identity catalog supersedes persisted display labels.
        if (names?.[slot]?.size) return names[slot].has(label);
        return hasNames
          ? explicitNames?.[slot] === label
          : source.length === target.length && slot === index;
      });
      if (candidates.length !== 1) {
        tile.bundleErrors.push(
          `Signal ${label} is ${candidates.length ? 'ambiguous' : 'missing'} on the connected bundle`,
        );
        continue;
      }
      graph.addEdge(source[index], candidates[0]);
      graph.addEdge(candidates[0], source[index]);
    }
  }
  const signalRoots = refreshBundleSignalCatalog(graph, tiles);
  // Dedicated adapters expose the full bundle at LEFT and one selected net at RIGHT.
  const interfaces = tiles.filter(
    (tile) => tile.tileType === TileType.BundleInterface && !tile.adapterPort,
  );
  for (const tile of interfaces) {
    tile.laneInputs = undefined;
    tile.laneOutputs = undefined;
    tile.meta.inputs[Side.RIGHT] = Signal.LOW;
    tile.meta.outputs[Side.RIGHT] = Signal.LOW;
    const config = BundleInterfaceStateSchema.safeParse(toJS(tile.state));
    const direction = getGlobalDirection(Side.LEFT, tile.meta.rotation)!;
    const neighbor = manager.get(
      getNeighborPosition(Point.fromData(tile.location), direction),
    );
    const neighborSide =
      neighbor && getLocalSide(opposite(direction), neighbor.meta.rotation)!;
    const lanes = neighbor && neighborSide && neighbor.laneNodeIds?.[neighborSide];
    if (
      !config.success ||
      !lanes?.length ||
      !enabled(tile, Side.LEFT) ||
      !enabled(neighbor!, neighborSide!)
    ) {
      tile.bundleErrors.push(
        'Connect the bundle port to a compact bundle and choose a valid bit',
      );
      continue;
    }
    const aliases = neighbor!.laneSignalIds?.[neighborSide!];
    tile.laneNodeIds = { [Side.LEFT]: [...lanes] };
    tile.laneSignalIds = { [Side.LEFT]: [...(aliases ?? [])] };
    tile.laneSignalLabels = {
      [Side.LEFT]: [...(neighbor!.laneSignalLabels?.[neighborSide!] ?? [])],
    };
    tile.bundleSignalLabels = { ...neighbor!.bundleSignalLabels };
    let selectedId = config.data.signalId;
    if (!selectedId && config.data.bit !== undefined) {
      const candidate = aliases?.[config.data.bit]?.[0];
      const matching = new Set(
        lanes
          .filter((_, slot) => candidate && aliases?.[slot]?.includes(candidate))
          .map((nodeId) => signalRoots.get(nodeId) ?? nodeId),
      );
      if (candidate && matching.size === 1) {
        selectedId = candidate;
        (tile.state as import('@/schemas/tiles').BundleInterfaceState).signalId =
          candidate;
      }
    }
    if (!selectedId) {
      tile.bundleErrors.push(
        config.data.bit !== undefined
          ? `Bit ${config.data.bit + 1} cannot be migrated to an unambiguous connected signal`
          : 'Choose a signal from the connected bundle',
      );
      continue;
    }
    const matching = new Set(
      lanes
        .filter((_, slot) => aliases?.[slot]?.includes(selectedId))
        .map((nodeId) => signalRoots.get(nodeId) ?? nodeId),
    );
    if (matching.size !== 1) {
      tile.bundleErrors.push(
        matching.size > 1
          ? 'The selected signal ID is ambiguous on different connected nets'
          : 'The selected signal is not present in this connected bundle',
      );
      continue;
    }
    tile.logicNodeIds = { [Side.RIGHT]: [...matching][0] };
  }
  // The adapter's scalar port keeps ordinary bidirectional WIRE semantics.
  for (const tile of interfaces) {
    const nodeId = tile.logicNodeIds?.[Side.RIGHT];
    if (!nodeId || !enabled(tile, Side.RIGHT)) continue;
    const direction = getGlobalDirection(Side.RIGHT, tile.meta.rotation)!;
    const neighbor = manager.get(
      getNeighborPosition(Point.fromData(tile.location), direction),
    );
    if (!neighbor) continue;
    const side = getLocalSide(opposite(direction), neighbor.meta.rotation)!;
    if (!enabled(neighbor, side)) continue;
    if (neighbor.meta.bundle || neighbor.laneNodeIds?.[side]) {
      tile.bundleErrors.push(
        'The scalar port connects to a scalar wire or chip pin; rotate the bundle interface',
      );
      continue;
    }
    const target = neighbor.logicNodeIds?.[side];
    const definition = tileDefinitionRegistry.get(neighbor.tileType);
    const io = neighbor.effectiveIoSides?.[side] ?? definition.ioSides[side];
    if (!target || !io || io.type === TileSideIOType.NONE) continue;
    if (io.type === TileSideIOType.INPUT || io.type === TileSideIOType.INPUT_OUTPUT)
      graph.addEdge(nodeId, target, {
        sourceTileId: tile.id,
        targetTileId: neighbor.id,
        targetSide: side,
      });
    if (io.type === TileSideIOType.OUTPUT || io.type === TileSideIOType.INPUT_OUTPUT)
      graph.addEdge(target, nodeId, {
        sourceTileId: neighbor.id,
        targetTileId: tile.id,
        targetSide: Side.RIGHT,
      });
  }
}

/** ID aliases describe physical Boolean nets; equal IDs never create connections. */
export function refreshBundleSignalCatalog(graph: LogicGraph, tiles: TileInstance[]) {
  const strict = tiles[0]?.context?.userPreferences?.bundleChipIdentityEnabled === true;
  // Catalog construction runs during physical named-port binding. An early
  // missing-owner diagnosis can resolve once a real adapter boundary connects.
  const missingOwner = 'The signal identity owner is missing or physically disconnected';
  for (const tile of tiles)
    tile.bundleErrors = filterVisualErrors(tile, tile.bundleErrors, missingOwner);
  const parent = new Map([...graph.nodes.keys()].map((id) => [id, id]));
  const find = (id: NodeHandle): NodeHandle => {
    let root = id;
    while (parent.get(root) && parent.get(root) !== root) root = parent.get(root)!;
    let current = id;
    while (parent.get(current) && parent.get(current) !== root) {
      const next = parent.get(current)!;
      parent.set(current, root);
      current = next;
    }
    return root;
  };
  for (const edge of graph.edges.values())
    if (
      graph.nodes.get(edge.sourceNodeId)?.type === 'WIRE' &&
      graph.nodes.get(edge.targetNodeId)?.type === 'WIRE'
    ) {
      const a = find(edge.sourceNodeId),
        b = find(edge.targetNodeId);
      if (a !== b) parent.set(b, a);
    }
  const identities = new Map<
    NodeHandle,
    Map<string, { label: string; width: number; tileId: string; lane: number }>
  >();
  for (const tile of tiles) {
    const config = tile.meta.bundle;
    if (!config?.signalIds || (config.signalOrigin && config.signalOrigin !== tile.id))
      continue;
    for (const [side, nodeIds] of Object.entries(tile.laneNodeIds ?? {}))
      for (let slot = 0; slot < nodeIds.length; slot++) {
        const lane = config.sides?.[side as Side]?.[slot] ?? slot,
          signalId = config.signalIds[lane];
        if (!signalId) continue;
        const root = find(nodeIds[slot]);
        let group = identities.get(root);
        if (!group) identities.set(root, (group = new Map()));
        const previous = group.get(signalId),
          candidate = {
            label: config.signalLabels?.[signalId] ?? signalId,
            width: config.width,
            tileId: tile.id,
            lane,
          };
        if (
          !previous ||
          candidate.width > previous.width ||
          (candidate.width === previous.width && candidate.tileId < previous.tileId)
        )
          group.set(signalId, candidate);
      }
  }
  for (const tile of tiles) {
    const carriesSelectorAliases =
      tile.cablePort &&
      Object.values(tile.laneSignalIds ?? {})
        .flat(2)
        .some(containsChipMemberIdentity);
    if (!tile.adapterPort && !(tile.cablePort && (strict || carriesSelectorAliases)))
      continue;
    const nodes = tile.laneNodeIds?.[Side.LEFT] ?? [];
    const ids = tile.adapterSignalIds ?? [];
    const labels = tile.adapterSignalLabels ?? {};
    if (tile.cablePort) {
      const sides =
        strict || carriesSelectorAliases
          ? Object.entries(tile.laneNodeIds ?? {})
          : ([[Side.LEFT, nodes]] as const);
      for (const [side, carriedNodes] of sides)
        carriedNodes.forEach((nodeId, lane) => {
          const root = find(nodeId);
          let group = identities.get(root);
          if (!group) identities.set(root, (group = new Map()));
          for (const id of tile.laneSignalIds?.[side as Side]?.[lane] ?? [])
            group.set(id, {
              label: tile.bundleSignalLabels?.[id] ?? '?',
              width: carriedNodes.length,
              tileId: tile.id,
              lane,
            });
        });
      continue;
    }
    nodes.forEach((nodeId, lane) => {
      const id = ids[lane];
      if (!id) return;
      const root = find(nodeId);
      let group = identities.get(root);
      if (!group) identities.set(root, (group = new Map()));
      for (const alias of [id, ...(tile.adapterSignalAliases?.[id] ?? [])])
        group.set(alias, {
          label: labels[id] ?? '?',
          width: nodes.length,
          tileId: tile.id,
          lane,
        });
    });
  }
  const catalogs = new Map<
    NodeHandle,
    { ids: string[]; labels: Record<string, string> }
  >();
  for (const [root, group] of identities) {
    const ordered = [...group].sort(
      (a, b) =>
        (strict
          ? Number(b[0].startsWith('adapter:[')) - Number(a[0].startsWith('adapter:['))
          : 0) ||
        Number(b[0].startsWith('adapter:')) - Number(a[0].startsWith('adapter:')) ||
        b[1].width - a[1].width ||
        a[1].tileId.localeCompare(b[1].tileId) ||
        a[1].lane - b[1].lane ||
        a[0].localeCompare(b[0]),
    );
    catalogs.set(root, {
      ids: ordered.map(([id]) => id),
      labels: Object.fromEntries(ordered.map(([id, entry]) => [id, entry.label])),
    });
  }
  const sharedLabels = new Map<string, Record<string, string>>();
  const labelKey = (tile: TileInstance) =>
    JSON.stringify(
      [
        ...new Set(
          Object.values(tile.laneNodeIds ?? {})
            .flat()
            .map(find),
        ),
      ].sort(),
    );
  for (const tile of tiles) {
    if (!tile.laneNodeIds) continue;
    const key = labelKey(tile);
    let labels = sharedLabels.get(key);
    if (!labels) sharedLabels.set(key, (labels = {}));
    Object.assign(labels, tile.meta.bundle?.signalLabels ?? {});
    for (const nodeId of Object.values(tile.laneNodeIds).flat())
      Object.assign(labels, catalogs.get(find(nodeId))?.labels ?? {});
  }
  for (const tile of tiles) {
    if (!tile.laneNodeIds) continue;
    const aliases: Partial<Record<Side, string[][]>> = {},
      labels: Partial<Record<Side, string[]>> = {};
    for (const [side, nodeIds] of Object.entries(tile.laneNodeIds)) {
      const catalog = nodeIds.map((id) => catalogs.get(find(id)));
      if (
        tile.meta.bundle?.signalOrigin &&
        nodeIds.length &&
        catalog.every((item) => !item?.ids.length)
      ) {
        const error = 'The signal identity owner is missing or physically disconnected';
        if (!tile.bundleErrors.includes(error)) tile.bundleErrors.push(error);
      }
      aliases[side as Side] = catalog.map((item) => item?.ids ?? []);
      labels[side as Side] = catalog.map((item) => item?.labels[item.ids[0]] ?? '?');
    }
    tile.laneSignalIds = aliases;
    tile.laneSignalLabels = labels;
    tile.bundleSignalLabels = sharedLabels.get(labelKey(tile));
  }
  return new Map([...graph.nodes.keys()].map((id) => [id, find(id)]));
}
